import type { FeatureKey, PrismicImage, PrismicLink, PrismicObject, StructuredText } from '../prismic/common';

export type { GameGridLayoutContext, GameViewModel } from './game-view-model';

export type Status = 'Active' | 'Disabled';
export type SocialTagBadgeState = 'hot' | 'cold';

export type GameProviderData = {
    name: StructuredText;
    provider_active_field: unknown | null;
    provider_config: unknown | null;
    providername: StructuredText[];
    display_name: string;
    status: Status;
    status_desktop: Status;
    status_mobile: Status;
    status_native: Status;
    game_loading_screen: unknown;
    country_filtering: string;
    licence_links: PrismicLink[];
};

export type GameProviderDataV2 = {
    providername: string;
    game_loading_screen: unknown;
    common_ui: unknown;
};

export type GameStudioData = {
    name: StructuredText;
    display_name: StructuredText;
    status_overall: Status;
    status_desktop: Status;
    status_mobile: Status;
    status_native: Status;
    common_ui: unknown;
    game_loading_screen: unknown;
};

export type GameStudioDataV2 = {
    display_name: string;
    common_ui: unknown;
    game_loading_screen: unknown;
};

export type GameDataV2 = Omit<
    GameData,
    // Fields whose shape changed in V2 (re-declared below with the new type)
    | 'display_name'
    | 'provider'
    | 'game_studio'
    | 'themes'
    | 'genres'
    | 'features'
    | 'objects'
    | 'video_trailer'
    | 'game_play_trailer'
    | 'fullscreen_game_play_trailer'
    | 'logo'
    | 'background'
    | 'flattened'
    // Fields absent from the V2 payload (encoded in metadata[] or dropped entirely)
    | 'volatility'
    | 'provider_name'
    | 'superclass'
    | 'class'
    | 'max_exposure'
    | 'bet_ways'
    | 'hit_frequency'
    | 'layout'
    | 'max_multiplier'
    | 'tax_category'
    | 'legal_reporting_category'
    | 'available_segment'
> & {
    display_name: string;
    provider: PrismicObject<'provider', GameProviderDataV2>;
    game_studio: PrismicObject<'game_studio', GameStudioDataV2>;
    metadata: string[];
    video_trailer: string;
    game_play_trailer: string;
    fullscreen_game_play_trailer: string;
    logo: string;
    background: string;
    flattened: string;
};

export type GameAnimatedTileAsset = {
    default_layout_url: PrismicLink;
    tall_portrait_url: PrismicLink;
    landscape_url: PrismicLink;
    enabled: boolean;
};

// TODO: Define status for anything that is with any or unkonwn type
export type GameData = {
    name: StructuredText[];
    display_name: StructuredText[];
    release_date: Date;
    internal_code: string;
    preload: boolean;
    rgs_code_desktop: string;
    rgs_code_mobile: string;
    feeds_code_desktop: string;
    feeds_code_mobile: string;
    provider: PrismicObject<'provider', GameProviderData>;
    provider_name: string;
    game_studio: PrismicObject<'game_studio', GameStudioData>;
    status_overall: Status;
    status_desktop: Status;
    status_mobile: Status;
    status_xsell_ios: Status;
    status_native: Status;
    id_native: string | null;
    id_native_xsell: string | null;
    game_window_width: number | null;
    game_window_height: number | null;
    native_landscape_only: boolean;
    rgi: string;
    feature_keys: FeatureKey[];
    superclass: any;
    class: any;
    volatility: any | null;
    max_exposure: any | null;
    bet_ways: any | null;
    hit_frequency: any | null;
    layout: any | null;
    max_multiplier: any | null;
    genres: any;
    themes: any;
    features: any[];
    objects: any;
    tax_category: any;
    legal_reporting_category: any;
    available_segment: any;
    logo: any;
    background: { image: PrismicImage }[];
    flattened: { image: PrismicImage }[];
    animated_tiles_assets?: GameAnimatedTileAsset[];
    common_ui: unknown;
    background_color: string;
    custom_background_color: string;
    label: GameLabel;
    copyright_text: string | null;
    video_trailer: any;
    game_play_trailer: PrismicLink;
    fullscreen_game_play_trailer: PrismicLink;
    game_loading_screen: StructuredText;
    native_description: StructuredText[];
    seo_game_description_headline: string;
    seo_meta_title: string;
    seo_meta_description: string;
    unique_player_count?: number;
    badge_state?: SocialTagBadgeState;
    trigger_game_name?: string;
    mean_sps_rank?: string;
    mean_sps?: string;
    is_new?: boolean;

    rtp_low: number;
    rtp_high: number;
    ganid: number | string;
    body: unknown; // What is this used for?
    isSiteWideJackpot?: boolean;
};

export type GameType =
    | 'animated'
    | 'trending'
    | 'daily-jackpot'
    | 'badge-image'
    | 'new'
    | 'explore-games-row'
    | 'simplified-row'
    | null;

export enum GameLabel {
    New = 'New',
    Exclusive = 'Exclusive',
    Jackpot = 'Jackpot'
}

/** Slim game shape for zone Snowplow (impression / scroll) over the RSC → client boundary. */
export type GameTrackingInfo = {
    uid: string;
    rank_order?: number | string | null;
    data: {
        ganid: number | string;
        display_name: StructuredText[] | string;
        internal_code: string;
        trigger_game_name?: string;
        mean_sps_rank?: string;
        mean_sps?: string;
        model_type?: string;
    };
};
export type Game = PrismicObject<'game', GameData | GameDataV2>;
